import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { ApiError, useCreateList, useDeleteList, useLists } from "../lib/api";
import { fmtDate, fmtInt } from "../lib/format";
import { useToast } from "../ui/Toast";

export function ListsIndexPage() {
  const lists = useLists();
  const create = useCreateList();
  const del = useDeleteList();
  const toast = useToast();
  const [name, setName] = useState("");
  const [confirmId, setConfirmId] = useState<number | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await create.mutateAsync({ name: name.trim() });
      setName("");
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "Create failed", "danger");
    }
  }

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-[18px] font-semibold">Lists</h1>
          <p className="text-[12px]" style={{ color: "var(--muted)" }}>
            Saved prospect lists, private to your org. Export as CSV or GeoJSON; the outreach board
            arrives in Phase 5.
          </p>
        </div>
        <form onSubmit={submit} className="flex items-center gap-2">
          <input
            className="input w-56"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="New list name"
            aria-label="New list name"
            data-testid="lists-new-name"
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={create.isPending || !name.trim()}
            data-testid="lists-create"
          >
            Create
          </button>
        </form>
      </div>
      <table className="tbl mt-4" data-testid="lists-table">
        <thead>
          <tr>
            <th>Name</th>
            <th className="r">Sites</th>
            <th>Created</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {lists.isLoading ? (
            <tr>
              <td colSpan={4} style={{ color: "var(--muted)" }}>
                Loading…
              </td>
            </tr>
          ) : null}
          {(lists.data?.items ?? []).map((l) => (
            <tr key={l.id}>
              <td>
                <Link
                  to="/lists/$id"
                  params={{ id: String(l.id) }}
                  className="font-medium"
                  data-testid="list-link"
                >
                  {l.name}
                </Link>
              </td>
              <td className="num r">{fmtInt(l.item_count)}</td>
              <td className="num">{fmtDate(l.created_at)}</td>
              <td className="r">
                {confirmId === l.id ? (
                  <span className="flex justify-end gap-1">
                    <button
                      type="button"
                      className="btn btn-sm"
                      style={{ borderColor: "var(--danger)", color: "var(--danger)" }}
                      onClick={async () => {
                        try {
                          await del.mutateAsync(l.id);
                          toast(`Deleted ${l.name}`);
                        } catch (err) {
                          toast(err instanceof ApiError ? err.message : "Delete failed", "danger");
                        }
                        setConfirmId(null);
                      }}
                    >
                      Confirm delete
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setConfirmId(null)}
                    >
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setConfirmId(l.id)}
                    aria-label={`Delete ${l.name}`}
                  >
                    Delete
                  </button>
                )}
              </td>
            </tr>
          ))}
          {lists.data && lists.data.items.length === 0 ? (
            <tr>
              <td colSpan={4} style={{ color: "var(--muted)" }}>
                No lists yet. Create one here, or use "Add all in view to list" on the map.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
